import { z, type ZodTypeAny } from "zod";

import type { AdminSetting, SettingValue } from "@/queries/adminSettings";

/**
 * Build a zod schema for a single setting row. The schema's input/output
 * type is always `{ value: SettingValue }` so the form layer can stay
 * generic; per-key range/regex rules from ADR-0011 are encoded here.
 */
export function schemaForSetting(setting: AdminSetting): ZodTypeAny {
  return z.object({ value: valueSchema(setting) });
}

export interface SettingFormValues {
  value: SettingValue;
}

function valueSchema(setting: AdminSetting): ZodTypeAny {
  switch (setting.value_type) {
    case "bool":
      return z.boolean();
    case "int":
      return z.coerce
        .number({ message: "errors.mustBeInteger" })
        .int({ message: "errors.mustBeInteger" })
        .refine((v) => intRange(setting.key, v), "errors.validationGeneric");
    case "float":
      return z.coerce
        .number({ message: "errors.validationGeneric" })
        .refine((v) => floatRange(setting.key, v), "errors.validationGeneric");
    case "decimal":
      // Sent as a string to avoid JS float precision drift on $-amounts.
      return z
        .string()
        .min(1, "errors.required")
        .refine((v) => /^-?\d+(\.\d+)?$/.test(v), "errors.validationGeneric")
        .refine(
          (v) => decimalRange(setting.key, Number(v)),
          "errors.mustBePositive",
        );
    case "str":
      return setting.key.startsWith("ibkr_live_")
        ? z.string().nullable().optional()
        : z.string().min(1, "errors.required");
    case "secret":
      return z.string().min(1, "errors.required");
    default:
      return z.unknown();
  }
}

function intRange(key: string, v: number): boolean {
  if (key === "database_pool_size") return v >= 1;
  if (key === "database_max_overflow") return v >= 0;
  if (key.endsWith("_port")) return v >= 1 && v <= 65_535;
  if (key.endsWith("_client_id")) return v >= 0;
  return true;
}

function floatRange(key: string, v: number): boolean {
  if (key === "trace_sampling_rate") return v >= 0 && v <= 1;
  return true;
}

function decimalRange(key: string, v: number): boolean {
  if (
    key === "anthropic_daily_budget_usd" ||
    key === "anthropic_per_call_max_usd"
  ) {
    return Number.isFinite(v) && v > 0;
  }
  return Number.isFinite(v);
}
